import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter, Link as RouterLink } from 'react-router-dom';

import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import AvatarGroup from '@mui/material/AvatarGroup';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Link from '@mui/material/Link';
import ListItem from '@mui/material/ListItem';
import Button from '@mui/material/Button';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import useTheme from '@mui/material/styles/useTheme';
import useMediaQuery from '@mui/material/useMediaQuery';

import {
  fetchOpenTeamDetailsByUris,
  fetchOpenTeamScoutProfile,
  fetchOpenRandomTeams,
} from '../../store/actions/team';
import { fetchOpenRandomInsts } from '../../store/actions/institute';
import { fetchOpenRandomUsers } from '../../store/actions/user';
import { getRandomInt, unEscapeHTML } from '../../utils/strings';
import {
  dispatchPageTitle,
  dispatchPageMetaTags,
  dispatchPageLinks,
} from '../../store/actions/common';
import { gt_config } from '../../configs';
import * as ROUTES from '../../constants/routes';
import {
  getTeamAvatar,
  getInstEmblem,
  getImage,
  getV0Image,
} from '../../utils/images';

const OpenTeamProfile = (props) => {
  const theme = useTheme();

  const { teamUri } = props.match.params;
  const [teamData, setTeamData] = useState(null);
  const [teamScout, setTeamScout] = useState(null);

  const [randomUsers, setRandomUsers] = useState([]);
  const [randomTeams, setRandomTeams] = useState([]);
  const [randomInsts, setRandomInsts] = useState([]);
  const [allRandomUsers, setAllRandomUsers] = useState([]);
  const [allRandomTeams, setAllRandomTeams] = useState([]);
  const [allRandomInsts, setAllRandomInsts] = useState([]);
  const isMobile = useMediaQuery('(max-width:600px)', { noSsr: true });
  const [showMoreContent, setShowMoreContent] = useState(!isMobile);
  const [scrollPosition, setScrollPosition] = useState(0);

  useEffect(() => {
    if (!showMoreContent) {
      window.addEventListener('scroll', handleScroll, { passive: true });

      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [scrollPosition]);

  const handleScroll = () => {
    const position = window.pageYOffset;
    setScrollPosition(position);
    console.log('position', position);

    if (position > 1) {
      setShowMoreContent(true);
    }
  };

  useEffect(() => {
    showMoreContent &&
      fetchOpenRandomInsts().then((data) => {
        setAllRandomInsts(data);
        setRandomInsts(
          data.splice(getRandomInt(data.length - 4), isMobile ? 2 : 3),
        );
      });
    showMoreContent &&
      fetchOpenRandomTeams().then((data) => {
        setAllRandomTeams(data);
        setRandomTeams(
          data.splice(getRandomInt(data.length - 4), isMobile ? 2 : 3),
        );
      });
    showMoreContent &&
      fetchOpenRandomUsers().then((data) => {
        setAllRandomUsers(data);
        setRandomUsers(
          data.splice(getRandomInt(data.length - 4), isMobile ? 2 : 3),
        );
      });

    fetchOpenTeamDetailsByUris(teamUri).then((resp) => {
      if (resp.hasOwnProperty(teamUri) && resp[teamUri].hasOwnProperty('uri')) {
        setTeamData(resp[teamUri]);
        props.dispatchPageTitle(
          `${unEscapeHTML(resp[teamUri].name)} | Global Talent`,
        );
        props.dispatchPageMetaTags([
          { name: 'robots', content: 'noarchive' },
          {
            name: 'description',
            content: `View profile of ${
              resp[teamUri].sports
            } team ${unEscapeHTML(
              resp[teamUri].name,
            )} on Global Talent. ${unEscapeHTML(
              resp[teamUri].bio,
            )}. Located in ${resp[teamUri].city_name} ${
              resp[teamUri].country_name
            }`,
          },
          { name: 'og:type', content: 'article' },
          {
            name: 'og:title',
            content: `${unEscapeHTML(resp[teamUri].name)} | Global Talent`,
          },
          { name: 'og:url', content: `${gt_config.host}info/team/${teamUri}` },
          { name: 'og:image', content: `${getTeamAvatar(resp[teamUri])}` },
          {
            name: 'og:description',
            content: `View profile of ${
              resp[teamUri].sports
            } team ${unEscapeHTML(
              resp[teamUri].name,
            )} on Global Talent. ${unEscapeHTML(
              resp[teamUri].bio,
            )}. Located in ${resp[teamUri].city_name} ${
              resp[teamUri].country_name
            }`,
          },
          { name: 'twitter:card', content: 'summary' },
          { name: 'twitter:site', content: '@globaltalent' },
          {
            name: 'twitter:title',
            content: `${unEscapeHTML(resp[teamUri].name)} | Global Talent`,
          },
          {
            name: 'twitter:description',
            content: `View profile of ${
              resp[teamUri].sports
            } team ${unEscapeHTML(
              resp[teamUri].name,
            )} on Global Talent. ${unEscapeHTML(
              resp[teamUri].bio,
            )}. Located in ${resp[teamUri].city_name} ${
              resp[teamUri].country_name
            }`,
          },
        ]);
        props.dispatchPageLinks([
          { rel: 'canonical', href: `${gt_config.host}info/team/${teamUri}` },
          {
            rel: 'alternate',
            hreflang: 'en-US',
            href: `${gt_config.host}info/team/${teamUri}`,
          },
          {
            rel: 'alternate',
            hreflang: 'en-IN',
            href: `${gt_config.host}info/team/${teamUri}`,
          },
          {
            rel: 'alternate',
            hreflang: 'sv-SE',
            href: `${gt_config.host}info/team/${teamUri}`,
          },
          {
            rel: 'alternate',
            hreflang: 'de-DE',
            href: `${gt_config.host}info/team/${teamUri}`,
          },
          {
            rel: 'alternate',
            hreflang: 'ja-JP',
            href: `${gt_config.host}info/team/${teamUri}`,
          },
          {
            rel: 'alternate',
            hreflang: 'es-ES',
            href: `${gt_config.host}info/team/${teamUri}`,
          },
          {
            rel: 'alternate',
            hreflang: 'x-default',
            href: `${gt_config.host}info/team/${teamUri}`,
          },
        ]);
      }
    });

    fetchOpenTeamScoutProfile(teamUri).then((scout) => {
      if (scout.hasOwnProperty('info')) setTeamScout(scout);
      else setTeamScout(null);
    });
  }, [teamUri, showMoreContent]);

  return (
    teamData && (
      <Box
        display="flex"
        justifyContent="center"
        sx={{
          width: '100%',
          maxWidth: '1100px',
          margin: 'auto',
          padding: '1rem',
          marginTop: '2rem',
          [theme.breakpoints.down('sm')]: {
            marginTop: '0',
          },
        }}
      >
        <Grid spacing={1} direction="row" container>
          <Grid item xs={12} sm={9}>
            <Grid spacing={1} direction="row" container>
              <Grid item xs={12}>
                <Card>
                  <CardMedia
                    component="img"
                    alt="cover"
                    height="140"
                    image={getV0Image(
                      isMobile
                        ? teamData.cover.replace('.jpg', '-small.jpg')
                        : teamData.cover,
                    )}
                  />
                  <CardContent>
                    <Grid spacing={1} direction="row" container>
                      <Grid item xs={12} sm={8}>
                        <Avatar
                          imgProps={{ loading: 'lazy' }}
                          sx={{
                            border: '1px solid gainsboro',
                            width: 120,
                            height: 120,
                            marginTop: '-80px',
                          }}
                          alt="Avatar"
                          src={
                            isMobile
                              ? getTeamAvatar(teamData, 'thumbnail')
                              : getImage(teamData)
                          }
                        />
                        <Typography
                          gutterBottom
                          variant="h5"
                          component="div"
                          pt={2}
                        >
                          {unEscapeHTML(teamData.name)}
                        </Typography>
                        {teamData.institute_uri && (
                          <Typography
                            variant="body1"
                            color="text.secondary"
                            noWrap={true}
                          >
                            Part of{' '}
                            <Link
                              component={RouterLink}
                              underline="none"
                              to={{
                                pathname: `/info/institute/${teamData.institute_uri}`,
                                state: { from: props.location },
                              }}
                            >
                              {teamData.institute_name}
                            </Link>
                          </Typography>
                        )}
                        <Typography variant="subtitle1">
                          {teamData.sports}, {teamData.city_name}{' '}
                          {teamData.country_name}
                        </Typography>
                        <Typography
                          variant="body1"
                          color="text.secondary"
                          noWrap={true}
                        >
                          {unEscapeHTML(teamData.bio)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        {showMoreContent && (
                          <AvatarGroup
                            max={4}
                            sx={{
                              [theme.breakpoints.down('sm')]: { float: 'left' },
                            }}
                          >
                            {teamData.all_members.map((obj) => (
                              <Link
                                component={RouterLink}
                                underline="none"
                                to={{
                                  pathname: `/info/user/${obj.uri}`,
                                  state: { from: props.location },
                                }}
                                sx={{ marginLeft: '-8px' }}
                              >
                                <Avatar
                                  imgProps={{ loading: 'lazy' }}
                                  alt={unEscapeHTML(obj.name)}
                                  title={unEscapeHTML(obj.name)}
                                  src={getImage(obj, 'thumbnail')}
                                />
                              </Link>
                            ))}
                          </AvatarGroup>
                        )}
                        <Link
                          underline="hover"
                          href={ROUTES.SIGN_UP}
                          sx={{
                            float: 'right',
                            [theme.breakpoints.down('sm')]: { float: 'left' },
                          }}
                        >
                          <Typography variant="body1" color="text.secondary">
                            View all members
                          </Typography>
                        </Link>
                        {teamData.all_members.map((obj) => (
                          <Link
                            component={RouterLink}
                            underline="none"
                            to={{
                              pathname: `/info/user/${obj.uri}`,
                              state: { from: props.location },
                            }}
                          ></Link>
                        ))}
                      </Grid>
                    </Grid>
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      disableElevation
                      component={RouterLink}
                      to={{
                        pathname: `${ROUTES.SIGN_UP}?followType=team&follow=${teamData['uri']}`,
                        state: { from: props.location },
                      }}
                      style={{
                        marginLeft: '8px',
                        fontSize: '11px',
                        padding: '6px 24px',
                        borderRadius: '3px',
                      }}
                    >
                      Follow
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
              {teamData.bio && (
                <Grid item xs={12}>
                  <Typography pt={1} pb={1} variant="h6">
                    About {unEscapeHTML(teamData.name)}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {unEscapeHTML(teamData.bio)}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                {teamData.scout != null &&
                  teamData.scout.hasOwnProperty('uri') && (
                    <Typography pt={3} variant="h6">
                      {teamData.sports} scout at {unEscapeHTML(teamData.name)}
                    </Typography>
                  )}
                {teamData.scout != null &&
                  teamData.scout.hasOwnProperty('uri') && (
                    <ListItem alignItems="flex-start" disablePadding>
                      <ListItemAvatar>
                        <Link
                          component={RouterLink}
                          underline="none"
                          to={{
                            pathname: `/info/user/${teamData.scout.uri}`,
                            state: { from: props.location },
                          }}
                        >
                          <Avatar
                            imgProps={{ loading: 'lazy' }}
                            alt={unEscapeHTML(teamData.scout.name)}
                            title={unEscapeHTML(teamData.scout.name)}
                            src={getImage(teamData.scout, 'thumbnail')}
                          />
                        </Link>
                      </ListItemAvatar>
                      <ListItemText
                        primary={unEscapeHTML(teamData.scout.name)}
                        secondary={
                          <>
                            <Typography
                              component="p"
                              variant="body2"
                              color="text.primary"
                              noWrap={true}
                            >
                              {teamData.scout.sports}
                            </Typography>
                            <Typography
                              component="p"
                              variant="body2"
                              color="text.primary"
                              noWrap={true}
                            >
                              {unEscapeHTML(teamData.scout.bio)}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                  )}
                {Object.keys(teamData.coaches).length > 0 && (
                  <Typography pt={3} variant="h6">
                    Coaches at {unEscapeHTML(teamData.name)}
                  </Typography>
                )}
                {Object.keys(teamData.coaches).length > 0 &&
                  Object.keys(teamData.coaches).map((obj) =>
                    teamData.coaches[obj].map((o) => (
                      <ListItem alignItems="flex-start" disablePadding>
                        <ListItemAvatar>
                          <Link
                            component={RouterLink}
                            underline="none"
                            to={{
                              pathname: `/info/user/${o.uri}`,
                              state: { from: props.location },
                            }}
                          >
                            <Avatar
                              imgProps={{ loading: 'lazy' }}
                              alt={unEscapeHTML(o.name)}
                              title={unEscapeHTML(o.name)}
                              src={getImage(o, 'thumbnail')}
                            />
                          </Link>
                        </ListItemAvatar>
                        <ListItemText
                          primary={unEscapeHTML(o.name)}
                          secondary={
                            <>
                              <Typography
                                component="p"
                                variant="body2"
                                color="text.primary"
                                noWrap={true}
                              >
                                {o.sports}
                              </Typography>
                              <Typography
                                component="p"
                                variant="body2"
                                color="text.primary"
                                noWrap={true}
                              >
                                {unEscapeHTML(o.bio)}
                              </Typography>
                            </>
                          }
                        />
                      </ListItem>
                    )),
                  )}
              </Grid>
              {teamScout && (
                <Grid item xs={12} sm={6}>
                  <Typography pt={2} pb={2} variant="h6">
                    Scout profile of {unEscapeHTML(teamData.name)}
                  </Typography>
                  <Card sx={{ padding: '1rem' }}>
                    <CardContent sx={{ padding: 0 }}>
                      <Typography variant="body1">
                        Sport:{' '}
                        {[
                          ...new Set(
                            teamScout.es_text.replace(/,\s*$/, '').split(', '),
                          ),
                        ].join(', ')}
                      </Typography>
                      {teamScout.info.about && (
                        <Typography
                          variant="body1"
                          color="text.secondary"
                          noWrap={true}
                        >
                          {teamScout.info.about}
                        </Typography>
                      )}
                      {teamScout.es_text_achievements && (
                        <Typography
                          variant="body1"
                          color="text.secondary"
                          noWrap={true}
                        >
                          Achievements:{' '}
                          {teamScout.es_text_achievements.replace(/,\s*$/, '')}
                        </Typography>
                      )}
                      {teamScout.info.scholarship_available && (
                        <Typography
                          variant="body1"
                          color="text.secondary"
                          noWrap={true}
                        >
                          Scholarship Available:{' '}
                          {teamScout.info.scholarship_available}
                        </Typography>
                      )}
                    </CardContent>
                    <CardActions>
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        disableElevation
                        component={RouterLink}
                        to={{
                          pathname: ROUTES.SIGN_UP,
                          state: { from: props.location },
                        }}
                      >
                        Join to view full profile
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              )}
              {!showMoreContent && (
                <Grid item xs={12} sx={{ textAlign: 'right' }}>
                  <Button
                    color="secondary"
                    disableElevation
                    onClick={(e) => setShowMoreContent(true)}
                  >
                    Load suggestions
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid item xs={12} sm={3}>
            {randomTeams.length > 0 && (
              <Grid spacing={2} direction="row" container>
                <Grid item sm={1} />
                <Grid item xs={12} sm={11}>
                  <Typography pt={1} pb={0} variant="h6">
                    Similar profiles
                  </Typography>
                </Grid>
                {randomTeams.length > 0 &&
                  randomTeams.map((t) => (
                    <>
                      <Grid item sm={1} />
                      <Grid item xs={12} sm={11}>
                        <Card sx={{ padding: '1rem' }}>
                          <Link
                            component={RouterLink}
                            color="black"
                            underline="none"
                            to={{
                              pathname: `/info/team/${t.uri}`,
                              state: { from: props.location },
                            }}
                          >
                            <Avatar
                              imgProps={{ loading: 'lazy' }}
                              sx={{
                                border: '1px solid gainsboro',
                                margin: 'auto',
                                width: 60,
                                height: 60,
                              }}
                              alt="Avatar"
                              src={getTeamAvatar(t, 'thumbnail')}
                            />
                          </Link>
                          <Typography
                            gutterBottom
                            variant="subtitle1"
                            component="div"
                            pt={1}
                            noWrap={true}
                          >
                            <Link
                              component={RouterLink}
                              color="black"
                              underline="none"
                              tto={{
                                pathname: `/info/team/${t.uri}`,
                                state: { from: props.location },
                              }}
                            >
                              {unEscapeHTML(t.name)}
                            </Link>
                          </Typography>
                          <Typography variant="subtitle2">
                            {t.sports} {t.city_name} {t.country_name}
                          </Typography>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            noWrap={true}
                          >
                            {unEscapeHTML(t.bio)}
                          </Typography>
                        </Card>
                      </Grid>
                    </>
                  ))}
                {allRandomTeams.map((t) => (
                  <Link
                    key={`team-${t.uri}`}
                    component={RouterLink}
                    underline="none"
                    to={{
                      pathname: `/info/team/${t.uri}`,
                      state: { from: props.location },
                    }}
                  ></Link>
                ))}
              </Grid>
            )}
            {randomInsts.length > 0 && (
              <Grid spacing={1} direction="row" container>
                <Grid item sm={1} />
                <Grid item xs={12} sm={11}>
                  <Typography pt={1} pb={0} variant="h6">
                    More Clubs
                  </Typography>
                </Grid>
                {randomInsts.length > 0 &&
                  randomInsts.map((i) => (
                    <>
                      <Grid item sm={1} />
                      <Grid item xs={12} sm={11}>
                        <Card sx={{ padding: '1rem' }}>
                          <Link
                            component={RouterLink}
                            color="black"
                            underline="none"
                            to={{
                              pathname: `/info/institute/${i.uri}`,
                              state: { from: props.location },
                            }}
                          >
                            <Avatar
                              imgProps={{ loading: 'lazy' }}
                              sx={{
                                border: '1px solid gainsboro',
                                margin: 'auto',
                                width: 60,
                                height: 60,
                              }}
                              alt="Avatar"
                              src={getInstEmblem(i, 'thumbnail')}
                            />
                          </Link>
                          <Typography
                            gutterBottom
                            variant="subtitle1"
                            component="div"
                            pt={1}
                            noWrap={true}
                          >
                            <Link
                              component={RouterLink}
                              color="black"
                              underline="none"
                              to={{
                                pathname: `/info/institute/${i.uri}`,
                                state: { from: props.location },
                              }}
                            >
                              {unEscapeHTML(i.name)}
                            </Link>
                          </Typography>
                          <Typography variant="subtitle2">
                            {i.sports} {i.city_name} {i.country_name}
                          </Typography>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            noWrap={true}
                          >
                            {unEscapeHTML(i.bio)}
                          </Typography>
                        </Card>
                      </Grid>
                    </>
                  ))}
                {allRandomInsts.map((i) => (
                  <Link
                    key={`institute-${i.uri}`}
                    component={RouterLink}
                    underline="none"
                    to={{
                      pathname: `/info/institute/${i.uri}`,
                      state: { from: props.location },
                    }}
                  ></Link>
                ))}
              </Grid>
            )}
            {randomUsers.length > 0 && (
              <Grid spacing={1} direction="row" container>
                <Grid item sm={1} />
                <Grid item xs={12} sm={11}>
                  <Typography pt={1} pb={0} variant="h6">
                    More Users
                  </Typography>
                </Grid>
                {randomUsers.length > 0 &&
                  randomUsers.map((u) => (
                    <>
                      <Grid item sm={1} />
                      <Grid item xs={12} sm={11}>
                        <Card sx={{ padding: '1rem' }}>
                          <Link
                            component={RouterLink}
                            color="black"
                            underline="none"
                            to={{
                              pathname: `/info/user/${u.uri}`,
                              state: { from: props.location },
                            }}
                          >
                            <Avatar
                              imgProps={{ loading: 'lazy' }}
                              sx={{
                                border: '1px solid gainsboro',
                                margin: 'auto',
                                width: 60,
                                height: 60,
                              }}
                              alt="Avatar"
                              src={getImage(u, 'thumbnail')}
                            />
                          </Link>
                          <Typography
                            gutterBottom
                            variant="subtitle1"
                            component="div"
                            pt={1}
                            noWrap={true}
                          >
                            <Link
                              component={RouterLink}
                              color="black"
                              underline="none"
                              to={{
                                pathname: `/info/user/${u.uri}`,
                                state: { from: props.location },
                              }}
                            >
                              {unEscapeHTML(u.name)}
                            </Link>
                          </Typography>
                          <Typography variant="subtitle2">{u.extra}</Typography>
                          <Typography variant="subtitle2">
                            {u.sports} {u.city_name} {u.country_name}
                          </Typography>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            noWrap={true}
                          >
                            {unEscapeHTML(u.bio)}
                          </Typography>
                        </Card>
                      </Grid>
                    </>
                  ))}
                {allRandomUsers.map((u) => (
                  <Link
                    key={`user-${u.uri}`}
                    component={RouterLink}
                    underline="none"
                    to={{
                      pathname: `/info/user/${u.uri}`,
                      state: { from: props.location },
                    }}
                  ></Link>
                ))}
              </Grid>
            )}
          </Grid>
        </Grid>
      </Box>
    )
  );
};

const mapStateToProps = (state) => ({
  teamInfo: state.team.info,
});

const mapDispatchToProps = {
  dispatchPageTitle,
  dispatchPageMetaTags,
  dispatchPageLinks,
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(OpenTeamProfile),
);
